import { config } from '@/lib/helpers/config.helpers'
import ClipboardJS from 'clipboard'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import httpClient from '../../api/httpClient'
import AutoComplete from '../../components/AutoComplete'
import PaymentList from '../../components/payments/PaymentList'
import Retry from '../../components/Retry'
import UserComments from '../../components/UserComments'
import { caseOf, map } from '../../lib'

new ClipboardJS('.btn')

const SpinnerView = () => (
  <div class="spinner-container">
    <div class="spinner" />
  </div>
)

export default {
  computed: {
    ...mapGetters('users', [
      'user',
      'CheatReportFilters',
      'clients',
      'updateStatus',
      'cheatReport',
      'tags',
      'isFetchingCheatReports',
      'payments',
    ]),
  },

  data() {
    return {
      userId: this.$route.params.id,
    }
  },

  methods: {
    ...mapActions('users', [
      'init',
      'updateField',
      'changeClient',
      'addAccount',
      'removeAccount',
      'updateCheatReportFilters',
      'reset',
      'fetchCheatingReport',
      'toggleShowUser',
      'getTags',
      'showAddTags',
      'addUserTag',
      'removeUserTag',
      'saveUserTags',
      'showConvertLink',
      'convertLink',
      'showStats',
      'setCopied',
      'setLink',
      'getPayments',
    ]),
    ...mapMutations('users', ['update']),

    async downloadPaymentPDF(payment) {
      try {
        const response = await httpClient.post(
          `/users/${this.userId}/payments/${payment.id}/pdf`,
          {},
          {
            responseType: 'blob',
          },
        )

        const downloadUrl = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = downloadUrl
        link.setAttribute('download', this.getPaymentFileName(payment))
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(downloadUrl)
      } catch (error) {
        console.error(error)
      }
    },
    getPaymentFileName(payment) {
      const today = new Date().toISOString().split('T')[0]
      return `payment-${payment.id}-${payment.payment_date}-${today}.pdf`
    },
  },
  async created() {
    const initPromise = this.init(this.userId)

    if (!document.getElementById('iframeScript')) {
      const el = document.createElement('script')

      el.addEventListener('load', () => {
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.id = 'iframeScript'
        script.src = `/scripts/iframeScript.js?version=${config.appVersion}`
        document.head.appendChild(script)
      })

      el.type = 'text/javascript'
      el.src = 'https://code.jquery.com/jquery-3.2.1.min.js'
      document.head.appendChild(el)
    }

    this.getTags()
    this.getPayments(this.userId)

    await initPromise

    if (this.$route.query.action === 'show-convert-link') {
      const unwatch = this.$watch(
        'user',
        (user) => {
          caseOf(user, {
            Done: (u) => {
              this.showConvertLink(u)
              unwatch()
            },
            _: () => {},
          })
        },
        { immediate: true },
      )
    }
  },

  render() {
    return (
      <div class="page">
        {caseOf(this.user, {
          Done: (user) => (
            <div class="user">
              <div class="card">
                <header>
                  <h2>
                    View user: {this.userId}
                    <router-link to={`/users/${this.userId}/edit`}>
                      <edit-icon />
                    </router-link>
                  </h2>
                </header>
                <ul class="tags user-tags small">
                  <li class="add-user-tag-btn">
                    <button onClick={() => this.showAddTags(user)}>Add Tags</button>
                  </li>
                  {map(user.user_tags, (tag) => (
                    <li key={tag.slug}>
                      <span>{tag.name}</span>
                      <button type="button" onClick={() => this.removeUserTag({ user, value: tag })}>
                        <cross-icon />
                      </button>
                    </li>
                  ))}
                </ul>
                {caseOf(user.toolbarState, {
                  ShowConvertLink: () => (
                    <form class="inline" onSubmit={(e) => (e.preventDefault(), this.convertLink(user))}>
                      <div>
                        <label for="link">Link:</label>
                        <div class="icon-input">
                          <link-icon />
                          <input
                            type="url"
                            placeholder="Link to convert"
                            id="link"
                            value={user.link}
                            onInput={(e) =>
                              this.setLink({
                                id: user.id,
                                userType: user.userType,
                                link: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                      <button type="submit" name="button" disabled={!user.link}>
                        Convert
                      </button>
                    </form>
                  ),
                  ShowAddTags: () => (
                    <AutoComplete
                      kind="users"
                      id="user-select-tags"
                      update={(value) => this.update({ path: 'tags.value', value })}
                      suggestions={this.tags.suggestions}
                      itemSelected={(value) => this.addUserTag({ user, value })}
                      value={this.tags.value}
                      clearOnSelection={true}
                      selectFromInput={true}
                    />
                  ),
                  Pending: () => (
                    <div class="spinner-container">
                      <div class="spinner" />
                    </div>
                  ),
                  _: () => null,
                })}
                {caseOf(user.deepLinkStatus, {
                  Idle: () => null,
                  Processing: () => (
                    <div class="deeplink">
                      <div class="spinner" />
                    </div>
                  ),
                  Done: () => (
                    <div class="deeplink">
                      <input type="text" value={user.convertedLink} />
                      <button class="btn" onClick={() => this.setCopied(user)} data-clipboard-text={user.convertedLink}>
                        <copy-icon />
                        <span class={user.deepLinkCopied ? 'deepLinkCopied' : 'vh'}>copied!</span>
                      </button>
                    </div>
                  ),
                  Failed: () => (
                    <div>
                      <p className="error">Try again with another link</p>
                    </div>
                  ),
                })}
                <div class="show-item">
                  <p>Email:</p>
                  <span class="wrap-text">{user.email}</span>

                  <p>Username:</p>
                  <span>{user.username}</span>

                  <p>Client:</p>
                  <span>{user.client.name}</span>

                  <p>Revenue Tier:</p>
                  <span>{user.revenue_tier.name}</span>

                  <p>TD site id:</p>
                  <span>{user.current_tradedoubler_account?.td_site_id}</span>

                  <p>Social Media Accounts:</p>
                  <ul class="unstyled">
                    {map(user.social_media, (account) => (
                      <li key={account.id}>
                        <div class="meta">
                          <p>
                            {account.type}: <span>{account.identifier}</span>
                            {account.state === 'connected' && <span> (connected)</span>}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <h2>Payments</h2>
                  <PaymentList payments={this.payments} onDownload={(payment) => this.downloadPaymentPDF(payment)} />

                  <CheatReport report={this.cheatReport} context={this} isFetchingCheatReports={this.isFetchingCheatReports} />
                  <UserComments />
                </div>
              </div>
            </div>
          ),
          Error: () => <Retry refresh={() => this.init(this.userId)} />,
          _: () => <SpinnerView />,
        })}
      </div>
    )
  },
}

const CheatReport = ({ report, context, isFetchingCheatReports }) => {
  return [
    <h2>Cheat Report</h2>,
    <form class="inline date-picker" onSubmit={(e) => (e.preventDefault(), context.fetchCheatingReport(context.CheatReportFilters))}>
      <div>
        <label for="cheat-report-start-date">Start Date</label>
        <div>
          <input
            id="cheat-report-start-date"
            placeholder="E.g. 2018-11-08"
            type="search"
            value={context.CheatReportFilters.startDate}
            onInput={(e) => context.updateCheatReportFilters({ field: 'startDate', value: e.target.value })}
          />
        </div>
      </div>
      <div>
        <label for="cheat-report-end-date">End Date</label>
        <div>
          <input
            id="cheat-report-end-date"
            placeholder="E.g. 2018-11-08"
            type="search"
            value={context.CheatReportFilters.endDate}
            onInput={(e) => context.updateCheatReportFilters({ field: 'endDate', value: e.target.value })}
          />
        </div>
      </div>
      <div>
        <button type="submit">Filter</button>
      </div>
    </form>,

    caseOf(report, {
      Some: (report) => [
        <p>Cheat Report:</p>,
        <table>
          <thead>
            <tr>
              <th>Clicks</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {map(report.tooManyClicks, (record) => (
              <tr key={record.name + record.description}>
                <td>{record.clicks}</td>
                <td>{record.name || '-'}</td>
                <td>{record.description || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>,

        <p>IPs:</p>,
        <table>
          <thead>
            <tr>
              <th>Clicks</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            {map(report.ipListing, (record) => (
              <tr key={record.ip + record.clicks}>
                <td>{record.clicks}</td>
                <td>{record.ip}</td>
              </tr>
            ))}
          </tbody>
        </table>,
      ],
      None: () => isFetchingCheatReports && <SpinnerView />,
    }),
  ]
}

// -- Utils

// const formatSEK =
//   typeof Intl !== "undefined"
//     ? new Intl.NumberFormat("sv-SE", {
//         style: "currency",
//         currency: "SEK",
//         minimumFractionDigits: 0
//       })
//     : { format: x => x };
